import React from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, ShieldCheckIcon, ZapIcon, AlertTriangleIcon, ArrowRightIcon, TrendingDownIcon, HistoryIcon, ActivityIcon } from 'lucide-react';
export function POTSPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div className="bg-white" data-id="element-1721">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-1722">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-1723"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-1724"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1725">
          <div className="max-w-3xl" data-id="element-1726">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1727">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1728">
                <span className="flex h-2 w-2 rounded-full bg-amber-400 mr-2" data-id="element-1729"></span>
                Traditional Voice Service
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-1730">
                Plain Old Telephone
                <br data-id="element-1731" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-1732">
                  Systems (POTS)
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8" data-id="element-1733">
                Traditional analog phone service delivered over copper wires.
                While legacy systems are being phased out, we help businesses
                manage their transition to modern alternatives or maintain
                critical analog connections.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1734">
                <button onClick={() => onNavigate('business-phone')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-1735">
                  Explore Replacements
                  <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1736" />
                </button>
                <button onClick={() => onNavigate('contact')} className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-4 rounded-full font-bold text-lg transition-colors backdrop-blur-sm inline-flex items-center justify-center" data-id="element-1737">
                  Consult an Expert
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Reality of POTS */}
      <section className="py-24 bg-white" data-id="element-1738">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1739">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1740">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1741">
              The Current State of Analog Voice
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1742">
              As major carriers decommission copper infrastructure, businesses
              face rising costs and decreasing reliability for traditional
              lines.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1743">
            {[{
            title: 'Rising Costs',
            desc: 'Maintenance of legacy copper networks is becoming increasingly expensive, with rates rising up to 500% in some areas.',
            icon: TrendingDownIcon,
            color: 'text-red-500',
            bg: 'bg-red-50'
          }, {
            title: 'Decommissioning',
            desc: 'FCC orders have paved the way for carriers to retire copper lines, making it harder to find support for legacy systems.',
            icon: HistoryIcon,
            color: 'text-amber-500',
            bg: 'bg-amber-50'
          }, {
            title: 'Limited Features',
            desc: 'Analog lines lack the advanced features, mobility, and integration capabilities of modern cloud-based systems.',
            icon: AlertTriangleIcon,
            color: 'text-orange-500',
            bg: 'bg-orange-50'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="p-8 rounded-3xl border border-slate-100 bg-slate-50" data-id="element-1744">
                <div className={`w-14 h-14 ${feature.bg} rounded-2xl flex items-center justify-center mb-6`} data-id="element-1745">
                  <feature.icon className={`w-7 h-7 ${feature.color}`} data-id="element-1746" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1747">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-1748">{feature.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Critical Use Cases */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden relative" data-id="element-1749">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" data-id="element-1750"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1751">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1752">
            <div data-id="element-1753">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1754">
                Critical Analog Connections
              </h2>
              <p className="text-lg text-slate-400 mb-8" data-id="element-1755">
                Some systems still rely on analog signals. We provide
                specialized solutions to keep these critical services running
                while modernizing your core voice network.
              </p>
              <div className="space-y-6" data-id="element-1756">
                {[{
                title: 'Fire & Security Alarms',
                desc: 'Ensuring compliance with local codes while transitioning to cellular or IP monitoring.'
              }, {
                title: 'Elevator Phones',
                desc: 'Reliable emergency communications that meet safety regulations.'
              }, {
                title: 'Fax Machines',
                desc: 'Seamless analog-to-digital conversion for legacy fax hardware.'
              }, {
                title: 'POS Terminals',
                desc: 'Maintaining connectivity for older credit card processing systems.'
              }].map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-1757">
                    <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center shrink-0 mt-1" data-id="element-1758">
                      <ShieldCheckIcon className="w-4 h-4 text-primary" data-id="element-1759" />
                    </div>
                    <div data-id="element-1760">
                      <h4 className="font-bold text-white" data-id="element-1761">{item.title}</h4>
                      <p className="text-sm text-slate-400" data-id="element-1762">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="relative" data-id="element-1763">
              <div className="rounded-3xl overflow-hidden shadow-2xl border border-white/10" data-id="element-1764">
                <img src="https://images.unsplash.com/photo-1534536281715-e28d76689b4d?w=800&h=600&fit=crop" alt="Old telephone equipment" className="w-full h-auto opacity-80" data-id="element-1765" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-primary p-8 rounded-2xl shadow-xl" data-id="element-1766">
                <div className="text-4xl font-black text-white mb-1" data-id="element-1767">70%</div>
                <div className="text-sm text-blue-100 font-medium" data-id="element-1768">
                  Average savings when
                  <br data-id="element-1769" />
                  switching from POTS
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white" data-id="element-1770">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1771">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-1772">
            Ready to Modernize Your Legacy Lines?
          </h2>
          <p className="text-xl text-slate-600 mb-10" data-id="element-1773">
            Don't wait for your lines to be cut or your bill to skyrocket. Let
            our experts audit your current POTS usage and design a future-proof
            path forward.
          </p>
          <button onClick={() => onNavigate('contact')} className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg" data-id="element-1774">
            Get a Free Bill Audit
          </button>
        </div>
      </section>
    </div>;
}